import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Link as LinkIcon, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { API } from '@/constants/bluemonk';

export const SubmitModal = ({ open, onOpenChange, item, actor, headers, onSuccess }) => {
  const [comment, setComment] = useState('');
  const [attachmentType, setAttachmentType] = useState('none');
  const [file, setFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!actor) {
      toast.error('Please select your identity first');
      return;
    }

    if (!comment.trim() && attachmentType === 'none') {
      toast.error('Please add a comment or attachment');
      return;
    }

    setLoading(true);
    try {
      if (attachmentType !== 'none') {
        let fileUrl = null;
        let fileName = null;

        if (attachmentType === 'file' && file) {
          const formData = new FormData();
          formData.append('file', file);
          const uploadRes = await axios.post(`${API}/upload`, formData, { headers });
          fileUrl = uploadRes.data.fileUrl;
          fileName = uploadRes.data.fileName;
        }

        await axios.post(`${API}/input-requests/${item.id}/submit`, {
          actor,
          type: attachmentType,
          fileUrl,
          fileName,
          linkUrl: attachmentType === 'link' ? linkUrl : null,
          textContent: attachmentType === 'text' ? comment : null,
          note: comment.trim() || null
        }, { headers });
      }

      if (comment.trim() && attachmentType === 'none') {
        await axios.post(`${API}/input-requests/${item.id}/submit`, {
          actor,
          type: 'text',
          textContent: comment,
          note: null
        }, { headers });
      }

      toast.success('Input provided successfully!');
      onSuccess();
      
      setComment('');
      setAttachmentType('none');
      setFile(null);
      setLinkUrl('');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-8">{item.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="p-3 bg-slate-50 rounded-lg text-sm">
            <p className="text-slate-600 italic">{item.whyNeeded}</p>
            <p className="text-slate-500 mt-2">
              <span className="font-medium">Required format:</span> {item.formatRequired}
            </p>
          </div>

          <div>
            <Label className="text-base font-medium">Your Input / Comment</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your input, ask questions, or provide context..."
              rows={4}
              className="mt-2"
              autoFocus
            />
          </div>

          <div className="border-t pt-4">
            <Label className="text-sm font-medium text-slate-600">Attach (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                variant={attachmentType === 'file' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAttachmentType(attachmentType === 'file' ? 'none' : 'file')}
              >
                <Upload className="h-4 w-4 mr-1" />
                File
              </Button>
              <Button
                type="button"
                variant={attachmentType === 'link' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAttachmentType(attachmentType === 'link' ? 'none' : 'link')}
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                Link
              </Button>
              <Button
                type="button"
                variant={attachmentType === 'shared-via-email' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAttachmentType(attachmentType === 'shared-via-email' ? 'none' : 'shared-via-email')}
              >
                <Mail className="h-4 w-4 mr-1" />
                Sent via Email
              </Button>
            </div>

            {attachmentType === 'file' && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <Input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
                {file && (
                  <p className="text-sm text-blue-600 mt-2">
                    Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            )}

            {attachmentType === 'link' && (
              <div className="mt-3">
                <Input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="Paste link here (Google Drive, Dropbox, etc.)"
                  className="bg-blue-50"
                />
              </div>
            )}

            {attachmentType === 'shared-via-email' && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                <Mail className="h-4 w-4 inline mr-2" />
                This will note that you've shared the information via email to your Content Vista contact.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || (!comment.trim() && attachmentType === 'none') || (attachmentType === 'file' && !file) || (attachmentType === 'link' && !linkUrl)}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? 'Submitting...' : 'Provide Input'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
